import type React from "react";
import { useMemo, useState } from "react";

import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import invariant from "ts-invariant";

import { useDeleteService, useGetManagerServices } from "api/services/appointment/hooks";
import { useProfileContext } from "contexts/ProfileContext";
import { useSnackbarContext } from "contexts/SnackbarContext/useSnackbarContext";
import { BaseButton } from "shared/atoms/BaseButton/BaseButton";
import { ConfirmationPopup } from "shared/atoms/ConfirmationPopup";
import { Notification } from "shared/atoms/Notification";
import { View } from "shared/containers/View";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";

export type FormStates = "default" | "saving" | "success" | "dirty";

export const ServicesView: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useProfileContext();
  const { t } = useTranslation();

  const [serviceToDelete, setServiceToDelete] = useState<number | undefined>();
  const [deletingServiceId, setDeletingServiceId] = useState<number | undefined>();
  const { showSnackbar } = useSnackbarContext();

  const deleteServiceMutation = useDeleteService();

  invariant(profile);

  // API requests
  const { data: services = [], isError, isLoading, refetch } = useGetManagerServices(profile.id);

  const deleteService = async (serviceId: number) => {
    setDeletingServiceId(serviceId);
    setServiceToDelete(undefined);

    try {
      await deleteServiceMutation.mutateAsync({ managerId: profile.id, serviceId });
      await refetch();
      showSnackbar({
        message: t("common.item_deleted", { item: t("common.service", { count: 1 }) }),
        severity: "success",
      });
    } catch (error) {
      showSnackbar({
        message: t("errors.generic"),
        severity: "error",
      });
    } finally {
      setDeletingServiceId(undefined);
    }
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: t("booking.services.name"),
        flex: 1,
        minWidth: 155,
      },
      {
        field: "description",
        headerName: t("booking.services.description"),
        flex: 1,
        minWidth: 155,
      },
      {
        field: "bookable_by_patients",
        headerName: t("booking.services.bookable_by_patients"),
        flex: 1,
        minWidth: 155,
        type: "boolean",
      },
      {
        field: "actions",
        type: "actions",
        width: 120,
        getActions: params => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label={t("buttons.edit")}
            onClick={() => navigate(`/calendar/services/edit/${params.row.id}`)}
            disabled={deletingServiceId === params.row.id}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={t("buttons.delete")}
            onClick={() => setServiceToDelete(params.row.id)}
            disabled={deletingServiceId === params.row.id}
          />,
        ],
      },
    ];
  }, []);

  return (
    <View
      title={t("booking.services.title")}
      actionButtons={[
        <BaseButton
          text={t("booking.services.new_service")}
          onClick={() => navigate("/calendar/services/new")}
          icon="plus"
          disabled={isLoading}
          size="large"
        />,
      ]}
    >
      {isError ? (
        <Notification variant="danger">{t("errors.generic")}</Notification>
      ) : (
        <MUIDataGrid
          columns={columns}
          rows={services}
          getRowId={row => row.id}
          loading={isLoading}
          rowCount={services.length}
          rowSelection={false}
          slots={{
            noRowsOverlay: () => <EmptyResultOverlay />,
            noResultsOverlay: () => <EmptyResultOverlay />,
          }}
          localeText={{
            noRowsLabel: "",
            noResultsOverlayLabel: "",
          }}
          getRowClassName={params => (params.row.id === deletingServiceId ? "row-disabled" : "")}
          sx={{
            "& .row-disabled": {
              opacity: 0.5,
              pointerEvents: "none",
            },
          }}
          disableColumnResize
          disableColumnMenu
          hideHeader
        />
      )}

      {serviceToDelete !== undefined && (
        <ConfirmationPopup
          title={t("booking.services.confirm_deletion_title")}
          body={t("booking.services.confirm_deletion", { name: services.find(s => s.id === serviceToDelete)?.name })}
          isOpen
          showCloseButton={false}
          onClickOutside={() => setServiceToDelete(undefined)}
          onCancel={() => setServiceToDelete(undefined)}
          onConfirm={() => deleteService(serviceToDelete)}
          primaryButtonProps={{ variant: "danger" }}
        />
      )}
    </View>
  );
};
